import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../hooks/useTenant';

// Type definitions - exported for use in step components
export interface Input {
  id?: string;
  category: 'funding' | 'staff' | 'equipment' | 'partnerships' | 'knowledge' | 'other';
  name: string;
  description?: string | null;
  quantity?: string | null;
  value?: number | null;
  sort_order: number;
}

export interface Activity {
  id?: string;
  name: string;
  description?: string | null;
  frequency?: string | null;
  responsible_party?: string | null;
  sort_order: number;
}

export interface Output {
  id?: string;
  activity_id?: string;
  name: string;
  description?: string | null;
  metric_type?: 'count' | 'percentage' | 'currency' | 'hours' | null;
  target_value?: number | null;
  actual_value?: number | null;
  unit?: string | null;
  sort_order: number;
}

export interface OutcomeIndicator {
  id?: string;
  outcome_id?: string;
  organization_id?: string;
  name: string;
  measurement_method?: string | null;
  baseline_value?: number | null;
  target_value?: number | null;
  actual_value?: number | null;
  target_date?: string | null;
  data_source?: string | null;
  status: 'not_started' | 'in_progress' | 'achieved' | 'not_achieved';
  sort_order?: number;
}

export interface Outcome {
  id?: string;
  timeframe: 'short_term' | 'medium_term' | 'long_term';
  name: string;
  description?: string | null;
  sort_order: number;
  indicators: OutcomeIndicator[];
}

export interface ImpactGoal {
  id?: string;
  logic_model_id?: string;
  organization_id?: string;
  name: string;
  description?: string | null;
  timeframe?: string | null;
  contribution_statement?: string | null;
  sort_order: number;
}

export interface LogicModel {
  id?: string;
  program_id: string;
  organization_id: string;
  version: number;
  name: string;
  status: 'draft' | 'active' | 'archived';
  inputs: Input[];
  activities: Activity[];
  outputs: Output[];
  outcomes: Outcome[];
  impact_goals: ImpactGoal[];
}

export type WizardStep = 'impact' | 'outcomes' | 'outputs' | 'activities' | 'inputs' | 'review';

interface LogicModelContextType {
  logicModel: LogicModel | null;
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // Navigation
  setCurrentStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  canNavigateTo: (step: WizardStep) => boolean;
  
  // Data operations
  initializeLogicModel: (programId: string, existingId?: string) => Promise<void>;
  updateImpactGoals: (goals: ImpactGoal[]) => void;
  updateOutcomes: (outcomes: Outcome[]) => void;
  updateOutputs: (outputs: Output[]) => void;
  updateActivities: (activities: Activity[]) => void;
  updateInputs: (inputs: Input[]) => void;
  saveLogicModel: () => Promise<void>;
  markStepComplete: (step: WizardStep) => void;
  applyTemplate: (template: any) => Promise<void>;
}

const LogicModelContext = createContext<LogicModelContextType | null>(null);

const STEPS_ORDER: WizardStep[] = ['impact', 'outcomes', 'outputs', 'activities', 'inputs', 'review'];

const STEP_LABELS: Record<WizardStep, string> = {
  impact: 'Impact Goal',
  outcomes: 'Outcomes',
  outputs: 'Outputs',
  activities: 'Activities',
  inputs: 'Inputs',
  review: 'Review',
};

export function LogicModelProvider({ children }: { children: ReactNode }) {
  const { organization } = useTenant();
  const [logicModel, setLogicModel] = useState<LogicModel | null>(null);
  const [currentStep, setCurrentStep] = useState<WizardStep>('impact');
  const [completedSteps, setCompletedSteps] = useState<WizardStep[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Initialize a new or existing logic model
  const initializeLogicModel = useCallback(async (programId: string, existingId?: string) => {
    if (!organization) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (existingId) {
        // Load existing logic model with all related data
        const { data: lm, error: lmError } = await supabase
          .from('logic_models')
          .select('*')
          .eq('id', existingId)
          .single();
        
        if (lmError) throw lmError;
        
        // Load all related data in parallel
        const [inputsRes, activitiesRes, outputsRes, outcomesRes, impactRes] = await Promise.all([
          supabase.from('inputs').select('*').eq('logic_model_id', existingId).order('sort_order'),
          supabase.from('activities').select('*').eq('logic_model_id', existingId).order('sort_order'),
          supabase.from('logic_model_outputs').select('*').eq('logic_model_id', existingId).order('sort_order'),
          supabase.from('outcomes').select('*').eq('logic_model_id', existingId).order('sort_order'),
          supabase.from('impact_goals').select('*').eq('logic_model_id', existingId).order('sort_order'),
        ]);
        
        // Load indicators for each outcome
        const outcomes = outcomesRes.data || [];
        const outcomesWithIndicators = await Promise.all(
          outcomes.map(async (outcome) => {
            const { data: indicators } = await supabase
              .from('outcome_indicators')
              .select('*')
              .eq('outcome_id', outcome.id);
            return { ...outcome, indicators: indicators || [] };
          })
        );
        
        setLogicModel({
          ...lm,
          inputs: inputsRes.data || [],
          activities: activitiesRes.data || [],
          outputs: outputsRes.data || [],
          outcomes: outcomesWithIndicators,
          impact_goals: impactRes.data || [],
        });
        
        // Mark steps as complete based on existing data
        const completed: WizardStep[] = [];
        if (impactRes.data && impactRes.data.length > 0) completed.push('impact');
        if (outcomesRes.data && outcomesRes.data.length > 0) completed.push('outcomes');
        if (outputsRes.data && outputsRes.data.length > 0) completed.push('outputs');
        if (activitiesRes.data && activitiesRes.data.length > 0) completed.push('activities');
        if (inputsRes.data && inputsRes.data.length > 0) completed.push('inputs');
        setCompletedSteps(completed);
        
      } else {
        // Create new logic model
        const { data: newLm, error: createError } = await supabase
          .from('logic_models')
          .insert({
            program_id: programId,
            organization_id: organization.organization_id,
            name: 'New Logic Model',
            version: 1,
            status: 'draft',
          })
          .select()
          .single();
        
        if (createError) throw createError;
        
        setLogicModel({
          ...newLm,
          inputs: [],
          activities: [],
          outputs: [],
          outcomes: [],
          impact_goals: [],
        });
        setCompletedSteps([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize logic model');
    } finally {
      setIsLoading(false);
    }
  }, [organization]);

  // Auto-save with debounce
  const debouncedSave = useCallback(() => {
    if (saveTimeout) clearTimeout(saveTimeout);
    const timeout = setTimeout(() => {
      saveLogicModel();
    }, 2000);
    setSaveTimeout(timeout);
  }, [saveTimeout]);

  // Save logic model to database
  const saveLogicModel = useCallback(async () => {
    if (!logicModel || !logicModel.id) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Update main logic model
      await supabase
        .from('logic_models')
        .update({
          name: logicModel.name,
          status: logicModel.status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', logicModel.id);
      
      // Note: Individual element saves happen in their respective update functions
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [logicModel]);

  // Update functions for each element type
  const updateImpactGoals = useCallback((goals: ImpactGoal[]) => {
    setLogicModel(prev => prev ? { ...prev, impact_goals: goals } : null);
    debouncedSave();
  }, [debouncedSave]);

  const updateOutcomes = useCallback((outcomes: Outcome[]) => {
    setLogicModel(prev => prev ? { ...prev, outcomes } : null);
    debouncedSave();
  }, [debouncedSave]);

  const updateOutputs = useCallback((outputs: Output[]) => {
    setLogicModel(prev => prev ? { ...prev, outputs } : null);
    debouncedSave();
  }, [debouncedSave]);

  const updateActivities = useCallback((activities: Activity[]) => {
    setLogicModel(prev => prev ? { ...prev, activities } : null);
    debouncedSave();
  }, [debouncedSave]);

  const updateInputs = useCallback((inputs: Input[]) => {
    setLogicModel(prev => prev ? { ...prev, inputs } : null);
    debouncedSave();
  }, [debouncedSave]);

  // Navigation helpers
  const nextStep = useCallback(() => {
    const currentIndex = STEPS_ORDER.indexOf(currentStep);
    if (currentIndex < STEPS_ORDER.length - 1) {
      setCurrentStep(STEPS_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    const currentIndex = STEPS_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const canNavigateTo = useCallback((step: WizardStep) => {
    const stepIndex = STEPS_ORDER.indexOf(step);
    const currentIndex = STEPS_ORDER.indexOf(currentStep);
    
    // Can always go back
    if (stepIndex <= currentIndex) return true;
    
    // Can go forward if all previous steps are complete
    const previousSteps = STEPS_ORDER.slice(0, stepIndex);
    return previousSteps.every(s => completedSteps.includes(s));
  }, [currentStep, completedSteps]);

  const markStepComplete = useCallback((step: WizardStep) => {
    setCompletedSteps(prev => 
      prev.includes(step) ? prev : [...prev, step]
    );
  }, []);

  // Apply a template to the current logic model
  const applyTemplate = useCallback(async (template: any) => {
    if (!logicModel?.id || !organization) return;

    setIsSaving(true);
    setError(null);

    try {
      // Insert impact goals
      for (const goalName of template.preview.impactGoals) {
        await supabase.from('impact_goals').insert({
          logic_model_id: logicModel.id,
          organization_id: organization.organization_id,
          name: goalName,
          sort_order: 0,
        });
      }

      // Insert outcomes (all as short_term by default)
      for (let i = 0; i < template.preview.outcomes.length; i++) {
        await supabase.from('outcomes').insert({
          logic_model_id: logicModel.id,
          organization_id: organization.organization_id,
          name: template.preview.outcomes[i],
          timeframe: i === 0 ? 'short_term' : i === 1 ? 'medium_term' : 'long_term',
          sort_order: i,
        });
      }

      // Insert outputs
      for (let i = 0; i < template.preview.outputs.length; i++) {
        await supabase.from('logic_model_outputs').insert({
          logic_model_id: logicModel.id,
          organization_id: organization.organization_id,
          name: template.preview.outputs[i],
          metric_type: 'count',
          sort_order: i,
        });
      }

      // Insert activities
      for (let i = 0; i < template.preview.activities.length; i++) {
        await supabase.from('activities').insert({
          logic_model_id: logicModel.id,
          organization_id: organization.organization_id,
          name: template.preview.activities[i],
          sort_order: i,
        });
      }

      // Insert inputs (categorize based on keywords)
      for (let i = 0; i < template.preview.inputs.length; i++) {
        const inputName = template.preview.inputs[i].toLowerCase();
        let category: 'funding' | 'staff' | 'equipment' | 'partnerships' | 'knowledge' | 'other' = 'other';
        if (inputName.includes('budget') || inputName.includes('grant') || inputName.includes('fund')) {
          category = 'funding';
        } else if (inputName.includes('staff') || inputName.includes('instructor') || inputName.includes('volunteer') || inputName.includes('worker')) {
          category = 'staff';
        } else if (inputName.includes('equipment') || inputName.includes('facility') || inputName.includes('space') || inputName.includes('unit') || inputName.includes('storage')) {
          category = 'equipment';
        } else if (inputName.includes('partner') || inputName.includes('network')) {
          category = 'partnerships';
        } else if (inputName.includes('curriculum') || inputName.includes('material') || inputName.includes('training')) {
          category = 'knowledge';
        }

        await supabase.from('inputs').insert({
          logic_model_id: logicModel.id,
          organization_id: organization.organization_id,
          name: template.preview.inputs[i],
          category,
          sort_order: i,
        });
      }

      // Reload all data
      const programId = logicModel.program_id;
      await initializeLogicModel(programId, logicModel.id);
      
      // Mark all steps complete
      setCompletedSteps(['impact', 'outcomes', 'outputs', 'activities', 'inputs']);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply template');
    } finally {
      setIsSaving(false);
    }
  }, [logicModel, organization, initializeLogicModel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) clearTimeout(saveTimeout);
    };
  }, [saveTimeout]);

  return (
    <LogicModelContext.Provider
      value={{
        logicModel,
        currentStep,
        completedSteps,
        isLoading,
        isSaving,
        error,
        setCurrentStep,
        nextStep,
        prevStep,
        canNavigateTo,
        initializeLogicModel,
        updateImpactGoals,
        updateOutcomes,
        updateOutputs,
        updateActivities,
        updateInputs,
        saveLogicModel,
        markStepComplete,
        applyTemplate,
      }}
    >
      {children}
    </LogicModelContext.Provider>
  );
}

export function useLogicModel() {
  const context = useContext(LogicModelContext);
  if (!context) {
    throw new Error('useLogicModel must be used within a LogicModelProvider');
  }
  return context;
}

export { STEPS_ORDER, STEP_LABELS };
